// Choix des jours d'absence : logique pure (sans React), testée par selection.test.ts. Une date tapée au clavier n'est jamais appliquée à moitié,
// « Du » après « Au » ne retire rien en silence.
// Tout est immuable : chaque fonction rend les nouveaux jours, triés, sans doublon.
import {
  addJours, dateComplete, dernierDuMois, estOuvre, jourCourt, jourSemaine, lundiDe, moisDe, plage, premierDuMois,
  type DateIso,
} from './dates';

export type Periode = [DateIso, DateIso];

// ── Grille ──────────────────────────────────────────────────────────────────────────────────────────────
/** Semaines (lundi→vendredi) qui couvrent le mois : 4 à 6 lignes de 5 dates. */
export function semainesDuMois(mois: DateIso): DateIso[][] {
  const dernier = dernierDuMois(mois);
  const sem: DateIso[][] = [];
  for (let lundi = lundiDe(premierDuMois(mois)); lundi <= dernier; lundi = addJours(lundi, 7)) {
    sem.push([0, 1, 2, 3, 4].map((i) => addJours(lundi, i)));
  }
  return sem;
}

/**
 * Semaines affichées : TOUJOURS 6, à partir de la première semaine qui contient un jour ouvré du mois. Un mois en occupe au plus 5 : la
 * semaine suivante est donc toujours visible, et une absence à cheval sur la fin du mois se choisit d'un seul geste (en octobre, on voit
 * aussi du 2 au 6 novembre).
 */
export function semaines6(mois: DateIso): DateIso[][] {
  const m = moisDe(mois);
  const sem = semainesDuMois(mois);
  if (!sem[0].some((d) => moisDe(d) === m)) sem.shift();
  while (sem.length < 6) {
    const lundi = addJours(sem[sem.length - 1][0], 7);
    sem.push([0, 1, 2, 3, 4].map((i) => addJours(lundi, i)));
  }
  return sem;
}
export const joursVisibles = (mois: DateIso): DateIso[] => semaines6(mois).flat();

/** Un jour qu'on peut choisir : lundi→vendredi, aujourd'hui compris, jamais le passé. */
export const selectionnable = (d: DateIso, aujourdhui: DateIso): boolean => estOuvre(d) && d >= aujourdhui;

/** Pourquoi un jour n'est pas choisissable (dit à la souris et au clavier), ou null. */
export function raisonNonSelectionnable(d: DateIso, aujourdhui: DateIso, long: (s: DateIso) => string): string | null {
  if (d < aujourdhui) return `${long(d)} est passé : on ne peut plus demander de remplacement pour ce jour.`;
  if (!estOuvre(d)) return `${long(d)} tombe un week-end : pas d'atelier ce jour-là.`;
  return null;
}

// ── Jours choisis ───────────────────────────────────────────────────────────────────────────────────────
const trier = (a: DateIso[]): DateIso[] => Array.from(new Set(a)).sort();

/** Ajoute (valeur vrai) ou retire (faux) ces dates ; les jours qu'on ne peut pas choisir sont ignorés. */
export function poser(jours: DateIso[], dates: DateIso[], valeur: boolean, aujourdhui: DateIso): DateIso[] {
  const ok = dates.filter((d) => selectionnable(d, aujourdhui));
  if (valeur) return trier([...jours, ...ok]);
  const retirer = new Set(ok);
  return jours.filter((d) => !retirer.has(d));
}

/** Jours choisis regroupés en périodes de jours ouvrés qui se suivent (le vendredi et le lundi suivant se suivent). */
export function periodes(jours: DateIso[]): Periode[] {
  const out: Periode[] = [];
  let cur: Periode | null = null;
  for (const d of trier(jours)) {
    if (cur && d === addJours(cur[1], jourSemaine(cur[1]) === 5 ? 3 : 1)) cur[1] = d;
    else { cur = [d, d]; out.push(cur); }
  }
  return out;
}

/** Point de départ de « Maj + clic » : le dernier jour ajouté ; sinon le jour choisi le plus proche avant `d` ; sinon le dernier jour choisi. */
export function depart(jours: DateIso[], ancre: DateIso | null, d: DateIso): DateIso | null {
  if (ancre && jours.includes(ancre)) return ancre;
  const avant = jours.filter((x) => x < d);
  if (avant.length) return avant[avant.length - 1];
  return jours.length ? jours[jours.length - 1] : null;
}

export interface Ajout { jours: DateIso[]; ajoutes: DateIso[]; plage: DateIso[]; ancre: DateIso }

/**
 * Maj + clic (ou Maj + Espace) : ajoute tous les jours ouvrés entre le point de départ et `d`, même dans un autre mois. N'enlève JAMAIS rien :
 * sans point de départ, il ajoute seulement `d`.
 */
export function etendre(jours: DateIso[], ancre: DateIso | null, d: DateIso, aujourdhui: DateIso): Ajout {
  const a = depart(jours, ancre, d);
  const p = (a ? plage(a, d, true) : [d]).filter((x) => selectionnable(x, aujourdhui));
  const ajoutes = p.filter((x) => !jours.includes(x));
  return { jours: poser(jours, p, true, aujourdhui), ajoutes, plage: p, ancre: d };
}

/** Un clic : le jour est choisi s'il ne l'était pas, retiré sinon. Même chose pour un glisser : tous les jours prennent la valeur du premier. */
export function basculerPlage(jours: DateIso[], a: DateIso, b: DateIso, aujourdhui: DateIso, imposee?: boolean): { jours: DateIso[]; valeur: boolean; plage: DateIso[] } {
  // `imposee` : un glisser a déjà montré son aperçu (ajout ou retrait) ; si une date tapée a changé l'état entre-temps, le geste garde ce qu'il avait annoncé.
  const valeur = imposee ?? !jours.includes(a);
  const p = plage(a, b, true).filter((x) => selectionnable(x, aujourdhui));
  return { jours: poser(jours, p, valeur, aujourdhui), valeur, plage: p };
}

/** Jour voisin au clavier : flèches gauche/droite = jour ouvré précédent/suivant (vendredi → lundi), haut/bas = une semaine. */
export function voisin(d: DateIso, touche: 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown'): DateIso {
  if (touche === 'ArrowUp') return addJours(d, -7);
  if (touche === 'ArrowDown') return addJours(d, 7);
  if (touche === 'ArrowRight') return addJours(d, jourSemaine(d) === 5 ? 3 : 1);
  return addJours(d, jourSemaine(d) === 1 ? -3 : -1);
}

/** Jours choisis qui ne sont pas à l'écran, comptés par mois : [['2026-10', 3], ['2026-11', 5]]. */
export function aussiChoisis(jours: DateIso[], visibles: DateIso[]): [string, number][] {
  const vus = new Set(visibles);
  const par: Record<string, number> = {};
  for (const d of jours) if (!vus.has(d)) par[moisDe(d)] = (par[moisDe(d)] ?? 0) + 1;
  return Object.keys(par).sort().map((k) => [k, par[k]]);
}

// ── Lignes « Du / Au » (une par période, synchronisées avec le calendrier) ──────────────────────────────
export type Bout = 'du' | 'au';

export interface ResultatPeriode {
  /** Les jours choisis après le changement. */
  jours: DateIso[];
  /** Rien n'a changé : aucun jour ouvré à venir dans cette période. */
  vide: boolean;
  /** La période telle qu'elle est appliquée (jours ouvrés, à venir), ou null si vide. */
  periode: Periode | null;
  /** L'autre bout a suivi (« Du » placé après « Au » : le champ qu'on vient de changer l'emporte). */
  ajustement: { bout: Bout; valeur: DateIso } | null;
  ajoutes: DateIso[];
  retires: DateIso[];
  /** Des jours passés ont été laissés de côté. */
  passes: boolean;
  /** Pourquoi rien n'a été appliqué (dit à côté de la ligne), ou null. */
  raison: string | null;
}

/**
 * Applique une période Du / Au. `index` = rang de la ligne dans periodes(jours), ou null pour la ligne vide (« nouvelle période »). `au` vide =
 * un seul jour. Le champ qu'on vient de changer l'emporte : si « Du » passe après « Au », « Au » prend la même date, et inversement.
 * Les jours qui restent dans la période ne bougent pas ; ceux qui n'y sont plus sont retirés (et la fonction les nomme).
 */
export function appliquerPeriode(
  jours: DateIso[], index: number | null, du: DateIso, au: DateIso | '', bout: Bout, aujourdhui: DateIso,
): ResultatPeriode {
  const base: ResultatPeriode = { jours, vide: true, periode: null, ajustement: null, ajoutes: [], retires: [], passes: false, raison: null };
  if (!dateComplete(du) || (au !== '' && !dateComplete(au))) return base;
  let a = du, b = au === '' ? du : au;
  let ajustement: ResultatPeriode['ajustement'] = null;
  if (a > b) {
    if (bout === 'du') { b = a; ajustement = { bout: 'au', valeur: b }; }
    else { a = b; ajustement = { bout: 'du', valeur: a }; }
  }
  const voulus = plage(a, b, true);
  const apres = voulus.filter((d) => selectionnable(d, aujourdhui));
  const passes = voulus.some((d) => d < aujourdhui);
  if (!apres.length) {
    const raison = b < aujourdhui
      ? (a === b ? `${jourCourt(a)} est passé` : 'Ces jours sont passés') + ' : on ne peut plus demander de remplacement.'
      : a === b ? `${jourCourt(a)} tombe un week-end : pas d'atelier ce jour-là.` : 'Cette période ne contient que des jours passés ou un week-end.';
    return { ...base, passes, raison };
  }
  const avant = index == null ? [] : (() => { const p = periodes(jours)[index]; return p ? plage(p[0], p[1], true) : []; })();
  const retires = avant.filter((d) => !apres.includes(d));
  const ajoutes = apres.filter((d) => !jours.includes(d));
  const nouveaux = poser(poser(jours, retires, false, '0000-01-01'), apres, true, aujourdhui);
  return { jours: nouveaux, vide: false, periode: [apres[0], apres[apres.length - 1]], ajustement, ajoutes, retires, passes, raison: null };
}

/** Retire une période entière (la croix de sa ligne). */
export function retirerPeriode(jours: DateIso[], index: number): { jours: DateIso[]; retires: DateIso[]; periode: Periode | null } {
  const p = periodes(jours)[index];
  if (!p) return { jours, retires: [], periode: null };
  const retires = plage(p[0], p[1], true);
  return { jours: jours.filter((d) => !retires.includes(d)), retires, periode: p };
}

/** Rang de la période qui contient ce jour (le focus la suit après une fusion ou une coupure), ou -1. */
export function rangPeriodeDe(jours: DateIso[], d: DateIso): number {
  return periodes(jours).findIndex((p) => p[0] <= d && d <= p[1]);
}

// ── Textes (un seul point final, même quand l'abréviation du mois en porte déjà un : « … au jeu. 3 déc. ») ──
export const phrase = (t: string): string => (/\.$/.test(t) ? t : `${t}.`);

/** « Du mer. 28 oct. au ven. 6 nov. : 8 jours choisis (7 ajoutés). » */
export function phraseAjout(p: DateIso[], ajoutes: number): string {
  if (!ajoutes) return 'Ces jours étaient déjà choisis.';
  if (p.length === 1) return phrase(`1 jour ajouté : ${jourCourt(p[0])}`);
  return phrase(`Du ${jourCourt(p[0])} au ${jourCourt(p[p.length - 1])} : ${p.length} jours choisis${ajoutes < p.length ? ` (${ajoutes} ajouté${ajoutes > 1 ? 's' : ''})` : ''}`);
}

/** « mer. 21 oct. → ven. 6 nov. » ou « mer. 21 oct. » */
export const libellePeriode = (p: Periode): string => (p[0] === p[1] ? jourCourt(p[0]) : `${jourCourt(p[0])} → ${jourCourt(p[1])}`);
/** « du mer. 21 oct. au ven. 6 nov. » ou « le mer. 21 oct. » (dans une phrase) */
export const nomPeriode = (p: Periode): string => (p[0] === p[1] ? `le ${jourCourt(p[0])}` : `du ${jourCourt(p[0])} au ${jourCourt(p[1])}`);
